import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ReasonLine } from "@/components/ui/disabled-reason";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiErrorDetail } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import {
  useResetConcept,
  useResetPreview,
  useUndoReset,
  type ResetResult,
  type ResetScope,
} from "@/hooks/useConceptReset";

/** Protection reasons, in the order the dialog lists them. */
const PROTECTION_LABELS: [string, string][] = [
  ["championed", "marked relevant"],
  ["thumbs_up", "thumbed up"],
  ["highlighted", "highlighted"],
  ["evidence", "cited as evidence"],
  ["notes", "with notes"],
  ["working_set", "in a working set"],
  ["tagged", "tagged"],
];

const UNDO_TOAST_MS = 12_000;

interface Props {
  open: boolean;
  onClose: () => void;
  conceptId: string;
  scope: ResetScope;
  /** Called after a successful reset (e.g. to start seed discovery). */
  onDone?: (result: ResetResult) => void;
}

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * Confirm dialog for "Start over" (scope "all") and "Clear seeds" (scope
 * "seeds"). Shows the server's preview counts — the protection rules live
 * server-side only — and offers Undo in the success toast.
 */
export function StartOverDialog({ open, onClose, conceptId, scope, onDone }: Props) {
  const preview = useResetPreview(conceptId, scope, open);
  const reset = useResetConcept(conceptId);
  const undo = useUndoReset(conceptId);

  const handleUndo = (resetId: string) => {
    undo.mutate(resetId, {
      onSuccess: () => toast.success("Reset undone"),
      onError: async (err) => toast.error(await apiErrorDetail(err, "Couldn't undo the reset")),
    });
  };

  const handleConfirm = () => {
    reset.mutate(scope, {
      onSuccess: (result) => {
        onClose();
        const summary =
          scope === "seeds"
            ? `Demoted ${plural(result.seeds_demoted, "seed")} to the corpus`
            : `Hid ${plural(result.hidden, "paper")}, kept ${result.kept} you've worked on`;
        toast.success(summary, {
          duration: UNDO_TOAST_MS,
          action: { label: "Undo", onClick: () => handleUndo(result.reset_id) },
        });
        onDone?.(result);
      },
      onError: async (err) => toast.error(await apiErrorDetail(err, "Couldn't start over")),
    });
  };

  const p = preview.data;
  const keptReasons = p
    ? PROTECTION_LABELS.filter(([key]) => (p.protected[key] ?? 0) > 0)
        .map(([key, label]) => `${p.protected[key]} ${label}`)
        .join(", ")
    : "";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{scope === "seeds" ? "Clear seed papers" : "Start over"}</DialogTitle>
          <DialogDescription>
            {scope === "seeds"
              ? "Your seed papers move back into the corpus as ordinary papers, so you can pick new seeds."
              : "Hide the papers this concept collected so you can re-seed and trawl again. Anything you've worked on stays."}
          </DialogDescription>
        </DialogHeader>

        {preview.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Checking your corpus…
          </div>
        ) : preview.isError || !p ? (
          <p className="text-sm text-destructive">Couldn't load the preview.</p>
        ) : (
          <ul className="space-y-1 text-sm" data-testid="start-over-preview">
            {scope === "all" && (
              <li>
                <span className="font-medium">{plural(p.hidden, "paper")}</span> will be hidden
              </li>
            )}
            {scope === "all" && p.kept > 0 && (
              <li>
                <span className="font-medium">{p.kept}</span> kept because you worked on them
                {keptReasons && <span className="text-muted-foreground"> ({keptReasons})</span>}
              </li>
            )}
            <li>
              <span className="font-medium">{plural(p.seeds_demoted, "seed")}</span> will move back
              into the corpus
              {p.seeds_skipped > 0 && (
                <span className="text-muted-foreground"> ({p.seeds_skipped} stay, already in the corpus)</span>
              )}
            </li>
            <li className="text-muted-foreground">
              Claims, drafts, notes and the concept description are untouched. You can undo this.
            </li>
          </ul>
        )}

        <DialogFooter className="items-center">
          {!p && (
            <ReasonLine id="start-over-why" className="mr-auto">
              {preview.isPending ? "Checking what this would clear…" : "Needs the preview above. Close and try again."}
            </ReasonLine>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={reset.isPending || !p}
            aria-describedby={!p ? "start-over-why" : undefined}
          >
            {reset.isPending ? "Working…" : scope === "seeds" ? "Clear seeds" : "Start over"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
